import { openNewChatComposer, resetNewChatComposer } from '@/lib/openHomeComposer';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalStore, useAuthStore, useCoworkerStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import { useRouter, useGlobalSearchParams } from 'expo-router';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { StyleSheet, Platform } from 'react-native';
import ChecklistProgressPill from '@/components/Checklist/ChecklistProgressPill';

const createStyles = (colors) => StyleSheet.create({
  headerText: {
    fontSize: 18,
    fontFamily: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif',
    fontWeight: 400,
    color: colors.text_color_primary,
  },
});

const HomeRouteNavigationHeaderRight = React.memo(() => {
  const { t } = useTranslation();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const params = useGlobalSearchParams();
  const isTemporaryChat = params?.['temporary-chat'] === 'true';

  const handleLogin = useCallback(() => {
    openModal('LoginModal');
  }, [openModal]);

  const handleSignup = useCallback(() => {
    openModal('GhostSignupModal');
  }, [openModal]);

  const handleNewChat = useCallback(() => {
    openNewChatComposer(router);
  }, [router]);

  const handleCreateNewTemporaryChat = useCallback(() => {
    resetNewChatComposer();
    if (isTemporaryChat) {
      router.replace('/app');
    } else {
      router.replace('/app?temporary-chat=true');
    }
  }, [router, isTemporaryChat]);

  if (isGhost) {
    return (
      <NavigationBarButtonContainer isGapped rightPadding>
        <NavigationBarButton
          text={t('common.signIn')}
          variant="secondary"
          size={isXS ? "small" : "medium"}
          onPress={handleLogin}
        />
        <NavigationBarButton
          text={t('common.signUp')}
          variant="primary"
          size={isXS ? "small" : "medium"}
          onPress={handleSignup}
        />
      </NavigationBarButtonContainer>
    );
  }

  return (
    <NavigationBarButtonContainer rightPadding>
      {hasUser && !isXS && Platform.OS === 'web' && <ChecklistProgressPill />}
      <NavigationBarButton
        icon="ghost"
        solidIcon={isTemporaryChat}
        title={t('nav.routes.home.newTemporaryChat')}
        tooltip={t('nav.routes.home.newTemporaryChatTooltip')}
        tooltipShortcut='⌘ ⇧ I'
        onPress={handleCreateNewTemporaryChat}
      />
    </NavigationBarButtonContainer>
  )
});

HomeRouteNavigationHeaderRight.displayName = 'HomeRouteNavigationHeaderRight';

const HomeHeaderTitle = React.memo(() => {
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const isWorkMode = useCoworkerStore((s) => s.composerMode === 'coworker');
  const params = useGlobalSearchParams();
  const isTemporaryChat = params?.['temporary-chat'] === 'true';
  const isNativeApp = Platform.OS === 'ios' || Platform.OS === 'android';
  // Same centered model pill as a normal new chat. Native temp chats keep it
  // even when the window is wider than the XS breakpoint (iPad / landscape).
  if (!isXS && !(isNativeApp && isTemporaryChat)) return null;

  // Work chooses its own model. Showing Auto/model controls implies a
  // setting the user can change when they cannot, so the header is just Work.
  if (isWorkMode) {
    return <Text style={styles.headerText}>{t('navigation.work')}</Text>;
  }

  return null;
});
HomeHeaderTitle.displayName = 'HomeHeaderTitle';

function ChatRouteNavigationHeaderLeft() {
  return <DefaultRouteNavigationHeaderLeft />
}

const HomeRouteNavigationOptions = {
  title: 'Gab AI',
  headerTitleAlign: 'center',
  headerTitle: () => <HomeHeaderTitle />,
  headerLeft: () => <ChatRouteNavigationHeaderLeft />,
  headerRight: () => <HomeRouteNavigationHeaderRight />
};

export default HomeRouteNavigationOptions;
